import { ButtonLinkBlockProps } from './types';
import { ButtonType } from '../Button/types';
import { ExternalVideoLayout } from '../ExternalVideo/types';
import { isLink } from './utils';
import { LinkType } from '../Links/types';
import { ModalSize } from '../Modal/types';
import Button from '../Button';
import ExternalVideo from '../ExternalVideo/ExternalVideo';
import Icon from '../Icon';
import InlineLink from '../Links/InlineLink';
import Modal from '../Modal';
import React, { useCallback, useState } from 'react';
import '@features/Components/ButtonLinkBlock/Scss/button-link-block.scss';
import {useTranslation} from "react-i18next";

function navigateToExternalUrl(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
}

function navigateToInternalUrl(url: string) {
    window.location.href = url;
}

function clickVideo(url: string, setDisplayVideoModal: Function) {
    setDisplayVideoModal(url)
}

function isExternal(props: ButtonLinkBlockProps): boolean {
    return !!props.ExternalLink;
}

function isDocumentLink(props: ButtonLinkBlockProps) {
    return props.DocumentLink && props.DocumentLink.Id;
}

function onClick(props: ButtonLinkBlockProps, setDisplayVideoModal: Function) {
    if (props.ExternalLink) {
        return navigateToExternalUrl(props.ExternalLink);
    }
    if (isDocumentLink(props)) {

        const url = props.DocumentLink && props.DocumentLink.Url;

        if (!url) {
            return;
        }
        return navigateToExternalUrl(url);
    }
    if (props.Link && props.Link.Expanded && props.Link.Expanded.RelativePath) {
        return navigateToInternalUrl(props.Link.Expanded.RelativePath);
    }
    if (props.VideoUrl) {
        return clickVideo(props.VideoUrl, setDisplayVideoModal);
    }

    return
}

function renderButtonIcon(props: ButtonLinkBlockProps) {
    if (isExternal(props)) {
        return <Icon iconName={'external-link'} size={18} aria-hidden="true"></Icon>;
    }
    if (isDocumentLink(props)) {
        return <Icon iconName={'download'} size={18} aria-hidden="true"></Icon>;
    }
    if (props.VideoUrl) {
        return <Icon iconName={'play-outlined'} size={18} aria-hidden="true"></Icon>;
    }

    return;
}

function renderButtonText(props: ButtonLinkBlockProps) {
    const icon = renderButtonIcon(props);

    return (
        <>
            <span>{props.LinkText}</span>
            {icon}
        </>
    );
}

function mapProps(
    props: ButtonLinkBlockProps,
    setDisplayVideoModal: Function
) {
  const {t} = useTranslation();
  const isExternalLink = isExternal(props);
  const isDocument = isDocumentLink(props);
  const opensInNewTabText = t('global.opensInNewTab');
  const ariaLabel = `${props.LinkText}${
    isExternalLink || isDocument ? ` (${opensInNewTabText})` : ''
  }`;
  const href =
    props.ExternalLink ||
    (isDocument && props.DocumentLink?.Url) ||
    props.Link?.Expanded?.RelativePath ||
    undefined;

  return {
    className: 'button-link-block',
    text: renderButtonText(props),
    type: Object.values(ButtonType)[Number(props.ButtonClass)],
    href,
    target: isExternalLink || isDocument ? '_blank' : undefined,
    rel: isExternalLink || isDocument ? 'noopener noreferrer' : undefined,
    onClick:
      props.VideoUrl
        ? () => setDisplayVideoModal(props.VideoUrl)
        : undefined,
    ariaLabel,
  };
}

function mapLinkProps(props: ButtonLinkBlockProps) {
    return {
        hideIcon: true,
        text: props.LinkText,
        type: LinkType.Intern,
        url: '',
    }
}

const ButtonLinkBlock = (props: ButtonLinkBlockProps) => {
    const [blockRef, setBlockRef] = useState<any>(undefined);
    const [displayVideoModal, setDisplayVideoModal]: [
        boolean | string,
        Function
    ] = useState(false);
    const [displayPopup, setDisplayPopup] = useState<boolean>(false);

    // Graph can return null for either the rich-text object or its HTML value.
    const popupHtml = props.PopupContent?.Html ?? '';
    const buttonProps = mapProps(props, setDisplayVideoModal);
    const linkProps = mapLinkProps(props);

    const onClickLink = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setDisplayPopup(true);
    }

    const onBlockRef = useCallback((node: any) => {
        if (node !== null && !blockRef) {
            setBlockRef(node);
        }
    }, []);

    return (
        <div className='button-link-block' ref={onBlockRef}>
            {isLink(props.ButtonClass) ? (
                <InlineLink {...linkProps} onClick={onClickLink} />
            ) : (
                <Button {...buttonProps}/>
            )}
            {props.VideoUrl && (
                <Modal
                    open={typeof displayVideoModal === 'string'}
                    handleClose={setDisplayVideoModal.bind(null, false)}
                    size={ModalSize.ExtraLarge}
                    noPadding={true}
                >
                    {typeof displayVideoModal === 'string' && (
                        <ExternalVideo
                            url={displayVideoModal}
                            layout={ExternalVideoLayout.Default}
                            autoPlay={true}
                        />
                    )}
                </Modal>
            )}
            {popupHtml && blockRef && (
                <Modal
                    open={displayPopup}
                    handleClose={() => setDisplayPopup(false)}
                    domNode={blockRef}
                    shouldCloseOnOverlayClick={false}
                >
                    <div className="button-link-block-popup-content"
                         dangerouslySetInnerHTML={{ __html: popupHtml }}>
                    </div>
                </Modal>
            )}
        </div>
    )
}

export default ButtonLinkBlock;
